import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { getSocket } from '../utils/socket';
import { playBuzz, playCorrect, playWrong, playReveal } from '../utils/sounds';
import type {
  QuestionPublic,
  GroupScore,
  GameStatus,
  RevealResult,
  IntruVotePayload,
  IntruRevealPayload,
  LobbyGroup,
} from '../types';

interface BuzzerState {
  taken: boolean;
  takenBy: string | null;
  takenByColor: string | null;
  myTurn: boolean;
}

interface GameContextType {
  groupId: number | null;
  groupName: string | null;
  groupColor: string | null;
  setGroupName: (name: string) => void;
  currentQuestion: QuestionPublic | null;
  gameStatus: GameStatus;
  gameJustStarted: boolean;
  scores: GroupScore[];
  scoreDeltas: Record<number, number>;
  timeLeftMs: number;
  buzzerState: BuzzerState;
  hasAnswered: boolean;
  isBlocked: boolean;
  waitingReveal: boolean;
  revealResult: RevealResult | null;
  myRevealResult: { correct: boolean | null; points: number } | null;
  intruVote: IntruVotePayload | null;
  intruReveal: IntruRevealPayload | null;
  lobbyGroups: LobbyGroup[];
  currentSerieName: string | null;
  serieEnded: boolean;
  submitText: (value: string) => Promise<{ success: boolean; error?: string }>;
  submitChoice: (optionIndex: number) => Promise<{ success: boolean; error?: string }>;
  submitIntruVote: (chosenQuestionId: number, justification: string) => Promise<{ success: boolean; error?: string }>;
  buzz: () => Promise<{ success: boolean; error?: string }>;
}

const GameContext = createContext<GameContextType>({} as GameContextType);

export function GameProvider({ children }: { children: React.ReactNode }) {
  const [groupId, setGroupId] = useState<number | null>(null);
  const [groupName, setGroupNameState] = useState<string | null>(
    () => localStorage.getItem('groupName')
  );
  const [groupColor, setGroupColor] = useState<string | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<QuestionPublic | null>(null);
  const [gameStatus, setGameStatus] = useState<GameStatus>('waiting');
  const [scores, setScores] = useState<GroupScore[]>([]);
  const [timeLeftMs, setTimeLeftMs] = useState(0);
  const [buzzerState, setBuzzerState] = useState<BuzzerState>({ taken: false, takenBy: null, takenByColor: null, myTurn: false });
  const [hasAnswered, setHasAnswered] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [waitingReveal, setWaitingReveal] = useState(false);
  const [revealResult, setRevealResult] = useState<RevealResult | null>(null);
  const [myRevealResult, setMyRevealResult] = useState<{ correct: boolean | null; points: number } | null>(null);
  const [intruVote, setIntruVote] = useState<IntruVotePayload | null>(null);
  const [intruReveal, setIntruReveal] = useState<IntruRevealPayload | null>(null);
  const [hasVotedIntru, setHasVotedIntru] = useState(false);
  const [lobbyGroups, setLobbyGroups] = useState<LobbyGroup[]>([]);
  const [currentSerieName, setCurrentSerieName] = useState<string | null>(null);
  const [serieEnded, setSerieEnded] = useState(false);
  const [gameJustStarted, setGameJustStarted] = useState(false);
  const [scoreDeltas, setScoreDeltas] = useState<Record<number, number>>({});

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const groupNameRef = useRef(groupName);
  const groupIdRef = useRef(groupId);
  const buzzerMyTurnRef = useRef(false);
  groupNameRef.current = groupName;
  groupIdRef.current = groupId;

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const startTimer = useCallback((ms: number) => {
    clearTimer();
    setTimeLeftMs(ms);
    if (ms <= 0) return;
    const end = Date.now() + ms;
    timerRef.current = setInterval(() => {
      const remaining = end - Date.now();
      if (remaining <= 0) {
        setTimeLeftMs(0);
        clearTimer();
      } else {
        setTimeLeftMs(remaining);
      }
    }, 100);
  }, [clearTimer]);

  const setGroupName = useCallback((name: string) => {
    localStorage.setItem('groupName', name);
    setGroupNameState(name);
  }, []);

  const resetQuestionState = useCallback(() => {
    setHasAnswered(false);
    setIsBlocked(false);
    setBuzzerState({ taken: false, takenBy: null, takenByColor: null, myTurn: false });
    buzzerMyTurnRef.current = false;
    setWaitingReveal(false);
    setRevealResult(null);
    setMyRevealResult(null);
    clearTimer();
  }, [clearTimer]);

  useEffect(() => {
    const socket = getSocket();

    function applyJoinData(data: any) {
      if (!data.success) return;
      if (data.group) {
        setGroupId(data.group.id);
        setGroupColor(data.group.color);
      }
      if (data.gameStatus) setGameStatus(data.gameStatus);
      if (data.scores) setScores(data.scores);
      if (data.currentQuestion) {
        setCurrentQuestion(data.currentQuestion);
        if (data.timeMs > 0) startTimer(data.timeMs);
      }
      if (data.intruVote) {
        setIntruVote(data.intruVote);
        setHasVotedIntru(false);
      }
    }

    const handleConnect = () => {
      const name = groupNameRef.current;
      if (name) {
        socket.emit('player:rejoin', { name }, () => {});
        socket.once('player:rejoin', applyJoinData);
      }
    };

    socket.on('connect', handleConnect);
    socket.on('player:join', applyJoinData);
    socket.on('player:rejoin', applyJoinData);

    socket.on('lobby:updated', ({ groups }: { groups: LobbyGroup[] }) => {
      setLobbyGroups(groups);
    });

    socket.on('game:started', () => {
      setGameStatus('in_progress');
      setGameJustStarted(true);
      setCurrentQuestion(null);
      setScores([]);
      setScoreDeltas({});
      setIntruVote(null);
      setIntruReveal(null);
      setHasVotedIntru(false);
      setCurrentSerieName(null);
      setSerieEnded(false);
      resetQuestionState();
    });

    socket.on('lobby:opened', () => {
      setGameStatus('waiting');
      setGameJustStarted(false);
      setCurrentQuestion(null);
      setScores([]);
      setScoreDeltas({});
      setIntruVote(null);
      setIntruReveal(null);
      setHasVotedIntru(false);
      setCurrentSerieName(null);
      setSerieEnded(false);
      resetQuestionState();
    });

    socket.on('game:ended', ({ scores: s }: { scores: GroupScore[] }) => {
      setGameStatus('ended');
      setScores(s);
      setCurrentQuestion(null);
      setCurrentSerieName(null);
      setSerieEnded(false);
      clearTimer();
    });

    socket.on('game:serie_started', ({ serieName }: { serieId: number; serieName: string }) => {
      setCurrentSerieName(serieName);
      setSerieEnded(false);
      setCurrentQuestion(null);
      setIntruVote(null);
      setIntruReveal(null);
      resetQuestionState();
    });

    socket.on('game:serie_ended', () => {
      setSerieEnded(true);
      setCurrentSerieName(null);
      setCurrentQuestion(null);
      clearTimer();
    });

    socket.on('question:start', ({ question, timeMs }: { question: QuestionPublic; timeMs: number }) => {
      setCurrentQuestion(question);
      setGameJustStarted(false);
      setIntruVote(null);
      setIntruReveal(null);
      setHasVotedIntru(false);
      setSerieEnded(false);
      resetQuestionState();
      if (timeMs > 0) startTimer(timeMs);
    });

    socket.on('question:end', () => {
      clearTimer();
      setTimeLeftMs(0);
      setWaitingReveal(true);
    });

    socket.on('question:reveal', (data: RevealResult) => {
      setRevealResult(data);
      setScores(data.scores);
      setWaitingReveal(false);

      // Build score deltas from reveal results
      const deltas: Record<number, number> = {};
      data.results.forEach((r) => { deltas[r.groupId] = r.points; });
      setScoreDeltas(deltas);

      const myId = groupIdRef.current;
      if (myId) {
        const myRes = data.results.find((r) => r.groupId === myId);
        if (myRes) {
          setMyRevealResult({ correct: myRes.correct, points: myRes.points });
          if (myRes.correct) playCorrect();
          else if (myRes.correct === false) playWrong();
        }
      }
      playReveal();
    });

    socket.on('scores:update', ({ scores: s }: { scores: GroupScore[] }) => {
      setScores(s);
    });

    socket.on('buzz:taken', ({ groupName: takenBy, groupColor: takenByColor, remainingMs }: {
      groupName: string; groupColor: string; remainingMs?: number;
    }) => {
      const myTurn = takenBy === groupNameRef.current;
      buzzerMyTurnRef.current = myTurn;
      setBuzzerState({ taken: true, takenBy, takenByColor, myTurn });
      // Stop timer for all players while admin validates
      clearTimer();
      if (myTurn) playBuzz();
    });

    socket.on('buzz:wrong', ({ remainingMs }: { remainingMs: number }) => {
      const wasMyTurn = buzzerMyTurnRef.current;
      buzzerMyTurnRef.current = false;
      setBuzzerState({ taken: false, takenBy: null, takenByColor: null, myTurn: false });
      // Resume timer with remaining time
      if (remainingMs > 0) startTimer(remainingMs);
      if (wasMyTurn) {
        setIsBlocked(true);
        playWrong();
      }
    });

    socket.on('buzz:reset', () => {
      setBuzzerState({ taken: false, takenBy: null, takenByColor: null, myTurn: false });
      buzzerMyTurnRef.current = false;
      setIsBlocked(false);
    });

    socket.on('serie:intru_vote', (data: IntruVotePayload) => {
      setIntruVote(data);
      setIntruReveal(null);
      setHasVotedIntru(false);
      setCurrentQuestion(null);
      setSerieEnded(false);
      clearTimer();
    });

    socket.on('serie:intru_reveal', (data: IntruRevealPayload) => {
      setIntruReveal(data);
      setScores(data.scores);
      setIntruVote(null);
      playReveal();
    });

    return () => {
      socket.off('connect', handleConnect);
      socket.off('player:join', applyJoinData);
      socket.off('player:rejoin', applyJoinData);
      socket.off('lobby:updated');
      socket.off('lobby:opened');
      socket.off('game:started');
      socket.off('game:ended');
      socket.off('game:serie_started');
      socket.off('game:serie_ended');
      socket.off('question:start');
      socket.off('question:end');
      socket.off('question:reveal');
      socket.off('scores:update');
      socket.off('buzz:taken');
      socket.off('buzz:wrong');
      socket.off('buzz:reset');
      socket.off('serie:intru_vote');
      socket.off('serie:intru_reveal');
      clearTimer();
    };
  }, [startTimer, resetQuestionState, clearTimer]);

  const submitText = useCallback((value: string): Promise<{ success: boolean; error?: string }> => {
    return new Promise((resolve) => {
      const socket = getSocket();
      socket.emit('answer:text', { value }, (res: any) => {
        if (res?.success) setHasAnswered(true);
        resolve(res ?? { success: false });
      });
    });
  }, []);

  const submitChoice = useCallback((optionIndex: number): Promise<{ success: boolean; error?: string }> => {
    return new Promise((resolve) => {
      const socket = getSocket();
      socket.emit('answer:choice', { optionIndex }, (res: any) => {
        if (res?.success) setHasAnswered(true);
        resolve(res ?? { success: false });
      });
    });
  }, []);

  const submitIntruVote = useCallback((chosenQuestionId: number, justification: string): Promise<{ success: boolean; error?: string }> => {
    return new Promise((resolve) => {
      const socket = getSocket();
      socket.emit('answer:intru', { chosenQuestionId, justification }, (res: any) => {
        if (res?.success) setHasVotedIntru(true);
        resolve(res ?? { success: false });
      });
    });
  }, []);

  const buzz = useCallback((): Promise<{ success: boolean; error?: string }> => {
    return new Promise((resolve) => {
      const socket = getSocket();
      playBuzz();
      socket.emit('buzz', {}, (res: any) => {
        resolve(res ?? { success: false });
      });
    });
  }, []);

  return (
    <GameContext.Provider
      value={{
        groupId,
        groupName,
        groupColor,
        setGroupName,
        currentQuestion,
        gameStatus,
        gameJustStarted,
        scores,
        scoreDeltas,
        timeLeftMs,
        buzzerState,
        hasAnswered,
        isBlocked,
        waitingReveal,
        revealResult,
        myRevealResult,
        intruVote: intruVote && !hasVotedIntru ? intruVote : (intruReveal ? null : intruVote),
        intruReveal,
        lobbyGroups,
        currentSerieName,
        serieEnded,
        submitText,
        submitChoice,
        submitIntruVote,
        buzz,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  return useContext(GameContext);
}
